import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Btn, Card, ConfirmModal, Heading, Modal, Spinner, ErrorState, StatusBadge } from '../../components/platform/ui';
import useAsync from '../../hooks/useAsync';
import { platformAdminApi, errorText } from '../../services/platformApi';
import { ImageField, VideoField, Text, Area, Select, Check, lines, DataTable, toLocalInput, fromLocalInput, EnTranslations, initEn, buildTranslations } from './shared';

const EN_COURSE = [
  { key: 'title', label: 'Title', kind: 'text' },
  { key: 'summary', label: 'Summary', kind: 'area', rows: 2 },
  { key: 'description', label: 'Description', kind: 'area', rows: 6 },
  { key: 'program', label: 'Program', kind: 'lines', rows: 4 },
  { key: 'author_name', label: 'Author', kind: 'text' },
];
const EN_LESSON = [
  { key: 'title', label: 'Title', kind: 'text' },
  { key: 'body', label: 'Lesson text', kind: 'area', rows: 8 },
];

const CourseForm = ({ initial, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [f, setF] = useState({
    title: '', kind: 'course', summary: '', description: '', price: 0, sort_order: 100, ...initial,
    slug: initial?.slug || '', cover_url: initial?.cover_url || '', author_name: initial?.author_name || '',
    _program: (initial?.program || []).join('\n'), _starts: toLocalInput(initial?.starts_at),
  });
  const [en, setEn] = useState(() => initEn(EN_COURSE, initial?.translations));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const body = {
      title: f.title, slug: f.slug || null, kind: f.kind, summary: f.summary, description: f.description, program: lines(f._program),
      cover_url: f.cover_url || null, author_name: f.author_name || null, price: Number(f.price) || 0,
      starts_at: fromLocalInput(f._starts), sort_order: Number(f.sort_order) || 0,
      translations: buildTranslations(EN_COURSE, en),
    };
    setBusy(true);
    try { initial?.id ? await platformAdminApi.updateCourse(initial.id, body) : await platformAdminApi.createCourse(body); toast.success(t('pl.admin.course.saved')); onSaved(); }
    catch (e) { toast.error(errorText(e, t('pl.admin.prod.saveFail'))); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={initial?.id ? t('pl.admin.course.titleEdit') : t('pl.admin.course.titleNew')}
      footer={(<><Btn variant="secondary" onClick={onClose}>{t('pl.common.cancel')}</Btn><Btn onClick={save} loading={busy}>{t('pl.common.save')}</Btn></>)}>
      <Select label={t('pl.admin.course.kind')} value={f.kind} onChange={set('kind')} options={[['course', t('pl.admin.course.kindCourse')], ['webinar', t('pl.admin.course.kindWebinar')]]} />
      <Text label={t('pl.admin.course.name')} value={f.title} onChange={set('title')} />
      <Text label={t('pl.admin.course.slug')} value={f.slug} onChange={set('slug')} disabled={!!initial?.id} />
      <Area label={t('pl.admin.course.summary')} rows={2} value={f.summary} onChange={set('summary')} />
      <Area label={t('pl.admin.course.description')} rows={6} value={f.description} onChange={set('description')} />
      <Area label={t('pl.admin.course.program')} rows={4} value={f._program} onChange={set('_program')} hint={t('pl.admin.course.programHint')} />
      <Text label={t('pl.admin.course.author')} value={f.author_name} onChange={set('author_name')} />
      <Text label={t('pl.admin.course.price')} type="number" min="0" step="0.01" value={f.price} onChange={set('price')} hint={t('pl.admin.course.priceHint')} />
      <Text label={t('pl.admin.course.starts')} type="datetime-local" value={f._starts} onChange={set('_starts')} />
      <ImageField label={t('pl.admin.course.cover')} value={f.cover_url} onChange={set('cover_url')} />
      <Text label={t('pl.admin.course.order')} type="number" value={f.sort_order} onChange={set('sort_order')} />
      <EnTranslations spec={EN_COURSE} value={en} onChange={setEn} />
    </Modal>
  );
};

const LessonForm = ({ courseId, initial, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [f, setF] = useState({ title: '', body: '', is_free_preview: false, is_published: true, ...initial, video_url: initial?.video_url || '', image_url: initial?.image_url || '' });
  const [en, setEn] = useState(() => initEn(EN_LESSON, initial?.translations));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const body = { title: f.title, body: f.body, video_url: f.video_url || null, image_url: f.image_url || null, is_free_preview: f.is_free_preview, is_published: f.is_published, translations: buildTranslations(EN_LESSON, en) };
    setBusy(true);
    try { initial?.id ? await platformAdminApi.updateLesson(initial.id, body) : await platformAdminApi.addLesson(courseId, body); toast.success(t('pl.admin.course.lessonSaved')); onSaved(); }
    catch (e) { toast.error(errorText(e, t('pl.admin.prod.saveFail'))); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={initial?.id ? t('pl.admin.course.lessonEdit') : t('pl.admin.course.lessonNew')}
      footer={(<><Btn variant="secondary" onClick={onClose}>{t('pl.common.cancel')}</Btn><Btn onClick={save} loading={busy}>{t('pl.common.save')}</Btn></>)}>
      <Text label={t('pl.admin.course.name')} value={f.title} onChange={set('title')} />
      <Area label={t('pl.admin.course.lessonText')} rows={8} value={f.body} onChange={set('body')} hint={t('pl.admin.course.lessonTextHint')} />
      <VideoField label={t('pl.admin.course.video')} value={f.video_url} onChange={set('video_url')} poster={f.image_url} />
      <ImageField label={t('pl.admin.course.image')} value={f.image_url} onChange={set('image_url')} />
      <Check label={t('pl.admin.course.freePreview')} value={f.is_free_preview} onChange={set('is_free_preview')} />
      <Check label={t('pl.admin.course.lessonPublished')} value={f.is_published} onChange={set('is_published')} />
      <EnTranslations spec={EN_LESSON} value={en} onChange={setEn} />
    </Modal>
  );
};

const CourseEditor = ({ id, onBack }) => {
  const { t } = useTranslation();
  const { data: c, loading, error, reload, setData } = useAsync(() => platformAdminApi.course(id), [id]);
  const [editL, setEditL] = useState(null);
  const [delL, setDelL] = useState(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  if (loading) return <Spinner />;
  if (error) return <ErrorState onRetry={reload} />;

  const move = async (idx, dir) => {
    const ids = c.lessons.map((l) => l.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { setData(await platformAdminApi.reorderLessons(id, ids)); } catch (e) { toast.error(errorText(e)); }
  };
  const removeLesson = async () => {
    setBusy(true);
    try { setData(await platformAdminApi.deleteLesson(delL.id)); toast.success(t('pl.admin.course.lessonDeleted')); setDelL(null); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const grant = async () => {
    try { await platformAdminApi.grantCourse(id, email); toast.success(t('pl.admin.course.accessGranted')); setEmail(''); }
    catch (e) { toast.error(errorText(e, t('pl.admin.course.accessFail'))); }
  };

  return (
    <div className="space-y-6">
      <Btn variant="secondary" onClick={onBack}>{t('pl.admin.course.backToList')}</Btn>
      <Heading level={2} className="text-2xl">{c.title}</Heading>
      <div className="flex justify-between items-center"><Heading level={3} className="text-xl">{t('pl.admin.course.lessons')}</Heading><Btn onClick={() => setEditL({})}>{t('pl.admin.course.addLesson')}</Btn></div>
      <DataTable empty={t('pl.admin.course.noLessons')} rows={c.lessons} columns={[
        { label: '№', key: 'position' },
        { label: t('pl.admin.course.colName'), render: (l) => <b>{l.title}</b> },
        { label: t('pl.admin.course.colTags'), render: (l) => (<div className="flex gap-1 flex-wrap">{l.is_free_preview && <StatusBadge tone="green">{t('pl.admin.course.tagFree')}</StatusBadge>}{!l.is_published && <StatusBadge>{t('pl.admin.course.tagHidden')}</StatusBadge>}{l.video_url && <StatusBadge tone="blue">{t('pl.admin.course.tagVideo')}</StatusBadge>}</div>) },
        { label: '', render: (l) => { const idx = c.lessons.findIndex((x) => x.id === l.id); return (
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => move(idx, -1)} aria-label={t('pl.admin.course.up')}><ArrowUp size={14} /></Btn>
            <Btn variant="secondary" onClick={() => move(idx, 1)} aria-label={t('pl.admin.course.down')}><ArrowDown size={14} /></Btn>
            <Btn variant="secondary" onClick={() => setEditL(l)}>{t('pl.common.edit')}</Btn>
            <Btn variant="secondary" onClick={() => setDelL(l)}>{t('pl.common.delete')}</Btn>
          </div>); } },
      ]} />
      <Card>
        <Heading level={3} className="text-lg mb-2">{t('pl.admin.course.grantTitle')}</Heading>
        <div className="flex gap-2 flex-wrap"><input className="border-2 border-black bg-white px-3 py-2 flex-1 min-w-[220px]" type="email" placeholder={t('pl.admin.course.userEmail')} value={email} onChange={(e) => setEmail(e.target.value)} /><Btn onClick={grant} disabled={!email}>{t('pl.admin.course.grant')}</Btn></div>
      </Card>
      {editL && <LessonForm courseId={id} initial={editL.id ? editL : null} onClose={() => setEditL(null)} onSaved={() => { setEditL(null); reload(); }} />}
      <ConfirmModal open={!!delL} busy={busy} onClose={() => setDelL(null)} onConfirm={removeLesson} confirmLabel={t('pl.common.delete')} text={t('pl.admin.course.deleteLessonConfirm', { title: delL?.title })} />
    </div>
  );
};

const AdminCourses = () => {
  const { t } = useTranslation();
  const { data, loading, error, reload } = useAsync(platformAdminApi.courses, []);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  if (open) return <CourseEditor id={open} onBack={() => { setOpen(null); reload(); }} />;

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errorText(e)); } };
  const remove = async () => {
    setBusy(true);
    try { await platformAdminApi.deleteCourse(del.id); toast.success(t('pl.admin.course.deleted')); setDel(null); reload(); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3"><Heading level={2} className="text-2xl">{t('pl.admin.course.section')}</Heading><Btn onClick={() => setEdit({})}>{t('pl.admin.course.add')}</Btn></div>
      {loading ? <Spinner /> : error ? <ErrorState onRetry={reload} /> : (
        <DataTable empty={t('pl.admin.course.empty')} rows={data} columns={[
          { label: t('pl.admin.course.colName'), render: (c) => <b>{c.title}</b> },
          { label: t('pl.admin.course.colType'), render: (c) => (c.kind === 'webinar' ? t('pl.admin.course.kindWebinar') : t('pl.admin.course.kindCourse')) },
          { label: t('pl.admin.course.colPrice'), render: (c) => (c.price > 0 ? `${c.price} ₽` : <StatusBadge tone="green">{t('pl.admin.course.free')}</StatusBadge>) },
          { label: t('pl.admin.course.colStatus'), render: (c) => (c.status === 'published' ? <StatusBadge tone="green">{t('pl.admin.course.statusPublished')}</StatusBadge> : <StatusBadge>{t('pl.admin.course.statusDraft')}</StatusBadge>) },
          { label: '', render: (c) => (
            <div className="flex flex-wrap gap-2">
              <Btn onClick={() => setOpen(c.id)}>{t('pl.admin.course.lessons')}</Btn>
              <Btn variant="secondary" onClick={() => setEdit(c)}>{t('pl.common.edit')}</Btn>
              {c.status === 'published'
                ? <Btn variant="secondary" onClick={() => act(() => platformAdminApi.unpublishCourse(c.id), t('pl.admin.course.unpublished'))}>{t('pl.admin.course.unpublish')}</Btn>
                : <Btn variant="secondary" onClick={() => act(() => platformAdminApi.publishCourse(c.id), t('pl.admin.course.publishedOk'))}>{t('pl.admin.course.publish')}</Btn>}
              <Btn variant="secondary" onClick={() => setDel(c)}>{t('pl.common.delete')}</Btn>
            </div>) },
        ]} />
      )}
      {edit && <CourseForm initial={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
      <ConfirmModal open={!!del} busy={busy} onClose={() => setDel(null)} onConfirm={remove} confirmLabel={t('pl.common.delete')} text={t('pl.admin.course.deleteConfirm', { title: del?.title })} />
    </div>
  );
};

export default AdminCourses;
